/**
 * ================================================================
 * COMPONENTE DE CONTROLE DE ACESSO
 * Renderiza conteúdo baseado em permissões
 * Data: 03/11/2025
 * ================================================================
 */

import React from 'react';
import { usePermission } from '../hooks/usePermission';

interface CanAccessProps {
  recurso: string;
  acao: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoading?: boolean;
}

/**
 * Componente que controla renderização baseada em permissões
 * 
 * @example
 * <CanAccess recurso="pacientes" acao="criar">
 *   <button>Novo Paciente</button>
 * </CanAccess>
 * 
 * @example
 * <CanAccess recurso="usuarios" acao="deletar" fallback={<span>Sem permissão</span>}>
 *   <button>Deletar Usuário</button>
 * </CanAccess>
 */
export const CanAccess: React.FC<CanAccessProps> = ({
  recurso,
  acao,
  children,
  fallback = null,
  showLoading = false
}) => {
  const { podeAcessar, carregando } = usePermission();
  
  // Mostrar loading enquanto carrega permissões
  if (carregando && showLoading) {
    return (
      <div className="text-muted">
        <i className="fas fa-spinner fa-spin"></i> Carregando...
      </div>
    );
  }
  
  // Verificar permissão
  if (!podeAcessar(recurso, acao)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

/**
 * Componente que esconde conteúdo se usuário NÃO tiver permissão
 * Alias para CanAccess com fallback=null
 */
export const ShowIf: React.FC<Omit<CanAccessProps, 'fallback'>> = (props) => {
  return <CanAccess {...props} fallback={null} />;
};

/**
 * Componente que mostra conteúdo apenas se usuário NÃO tiver permissão
 * Útil para mensagens de "sem permissão"
 */
export const HideIf: React.FC<Omit<CanAccessProps, 'fallback'>> = ({
  recurso,
  acao,
  children
}) => {
  const { podeAcessar, carregando } = usePermission();
  
  if (carregando) return null;
  
  // Inverte a lógica: mostra se NÃO tiver permissão
  if (podeAcessar(recurso, acao)) {
    return null;
  }
  
  return <>{children}</>;
};

/**
 * Componente que renderiza baseado no cargo do usuário
 * 
 * @example
 * <ForCargo cargos={['admin', 'psicologo']}>
 *   <button>Criar Prontuário</button>
 * </ForCargo>
 */
interface ForCargoProps {
  cargos: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const ForCargo: React.FC<ForCargoProps> = ({
  cargos,
  children,
  fallback = null
}) => {
  const { cargo, carregando } = usePermission();
  
  if (carregando) return null;
  
  if (!cargos.includes(cargo)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

/**
 * Componente que renderiza baseado no tipo de empresa
 * 
 * @example
 * <ForTipoEmpresa tipos={['clinica']}>
 *   <button>Relatórios Consolidados</button>
 * </ForTipoEmpresa>
 */
interface ForTipoEmpresaProps {
  tipos: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const ForTipoEmpresa: React.FC<ForTipoEmpresaProps> = ({
  tipos,
  children,
  fallback = null
}) => {
  const { tipoEmpresa, carregando } = usePermission();
  
  if (carregando) return null;
  
  if (!tipos.includes(tipoEmpresa)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};
